import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BytesToSizePipe, SafePipe, SecondsToMinutesPipe } from '@fb-tool-front/shared/pipes';
import {
  TuiButtonModule,
  TuiDataListModule,
  TuiDropdownModule,
  TuiErrorModule,
  TuiGroupModule,
  TuiHintModule,
  TuiHostedDropdownModule,
  TuiLabelModule,
  TuiLinkModule,
  TuiLoaderModule,
  TuiNotificationModule,
  TuiPrimitiveCheckboxModule,
  TuiPrimitiveTextfieldModule,
  TuiScrollbarModule,
  TuiSvgModule,
  TuiTextfieldControllerModule,
  TuiTooltipModule
} from '@taiga-ui/core';
import {
  TuiAccordionModule,
  TuiArrowModule,
  TuiBadgeModule,
  TuiCheckboxBlockModule,
  TuiCheckboxLabeledModule,
  TuiComboBoxModule,
  TuiDataListWrapperModule,
  TuiInputCountModule,
  TuiInputModule,
  TuiInputNumberModule,
  TuiIslandModule,
  TuiMultiSelectModule,
  TuiRadioBlockModule,
  TuiSelectModule,
  TuiTabsModule,
  TuiToggleModule,
  TuiTextAreaModule,
  TuiCheckboxModule,
  TuiLazyLoadingModule,
  TuiLineClampModule,
  TuiInputSliderModule,
  TuiInputPasswordModule,
  TuiFilterByInputPipeModule,
  TuiInputTagModule,
  TuiBadgedContentModule,
  TuiProgressModule,
  TuiTagModule,
  TuiInputPhoneInternationalModule,
  TuiInputInlineModule,
  TuiInputDateModule,
  TuiFieldErrorPipeModule,
  TuiInputFilesModule,
  TuiInputDateRangeModule,
  TuiDataListDropdownManagerModule,
  TuiRadioListModule,
  TuiInputDateTimeModule,
  TuiInputTimeModule,
  TuiTilesModule
} from '@taiga-ui/kit';
import { ComingSoonCardComponent } from './components/coming-soon-card/coming-soon-card.component';
import { CustomDialogModule } from './components/dialog/dialog.module';
import { RouterModule } from '@angular/router';
import {
  TuiActiveZoneModule,
  TuiAutoFocusModule,
  TuiElementModule,
  TuiFilterPipeModule,
  TuiFocusableModule,
  TuiForModule,
  TuiHoveredModule,
  TuiLetModule,
  TuiMapperPipeModule,
  TuiPreventDefaultModule
} from '@taiga-ui/cdk';
import { TuiMobileTabsModule, TuiSidebarModule } from '@taiga-ui/addon-mobile';
import { AitColorSelectorModule } from './components/color-picker-custom/color-selector/color-selector.module';
import { AitInputColorModule } from './components/color-picker-custom/input-color/input-color.module';
import { AitColorPickerModule } from './components/color-picker-custom/color-picker/color-picker.module';
import { AitColorEditModule } from './components/color-picker-custom/color-edit/color-edit.module';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NothingFoundComponent } from '@fb-tool-front/shared/components/nothing-found/nothing-found.component';
import { TrimDirective } from './directives/trim/trim.directive';
import { TuiTableModule } from '@taiga-ui/addon-table';
import { IntersectionObserverModule } from '@ng-web-apis/intersection-observer';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { LazyImageComponent } from '@fb-tool-front/shared/components/lazy-image/lazy-image.component';
import { GravityButtonComponent } from './components/gravity-button/gravity-button.component';
import { AitColorOptionsControllerModule } from './components/color-picker-custom/directives/color-options-controller/color-options-controller.module';
import { FilterPanelComponent } from '@fb-tool-front/shared/components/filter-panel/filter-panel.component';
import { ThemeNightToggleDirective } from '@fb-tool-front/theme-night/theme-night-toggle.directive';
import { AmplitudeDirective } from '@fb-tool-front/shared/directives/amplitude/amplitude.directive';
import { MobileModalComponent } from './components/mobile-modal/mobile-modal.component';
import { GtmDirective } from './directives/gtm/gtm.directive';
import { YoutubeEmbeddedComponent } from './components/youtube-embedded/youtube-embedded.component';
import { LocaleToggleDirective } from './directives/locale-toggle.directive';
import { TranslateModule } from '@ngx-translate/core';
import { RecaptchaFormsModule, RecaptchaModule } from 'ng-recaptcha';
import { DemoRequestComponent } from '@fb-tool-front/auth/demo-request/demo-request.component';
import { CookiesModalComponent } from '@fb-tool-front/shared/components/cookies-modal/cookies-modal.component';
import { DeleteAccountModalComponent } from '@fb-tool-front/shared/components/delete-account-modal/delete-account-modal.component';
import { PricingModalComponent } from '@fb-tool-front/shared/components/pricing-modal/pricing-modal.component';
import { OverlayRuleComponent } from '@fb-tool-front/shared/components/overlay-rule/overlay-rule.component';
import { CountriesComponent } from './components/countries/countries.component';
import { OverlaySelectorComponent } from './components/overlay-selector/overlay-selector.component';
import { RuleConditionsComponent } from './rule-conditions/rule-conditions.component';
import { OnlyCountriesPipe } from '@fb-tool-front/shared/pipes/only-countries.pipe';
import { TuiEditorModule, TuiPaletteModule } from '@taiga-ui/addon-editor';
import { AitPaletteModule } from '@fb-tool-front/shared/components/color-picker-custom/palette/palette.module';
import { OverlayTagsComponent } from '@fb-tool-front/shared/components/overlay-tags/overlay-tags.component';
import { TabsIosDirective } from '@fb-tool-front/shared/directives/tabs-ios.directive';
import { PolymorpheusModule } from '@tinkoff/ng-polymorpheus';
import { TuiCurrencyPipeModule } from '@taiga-ui/addon-commerce';
import { TuiLineDaysChartModule, TuiAxesModule, TuiLineChartModule } from '@taiga-ui/addon-charts';
import { NgChartsModule } from 'ng2-charts';
import { ResizeObserverModule } from '@ng-web-apis/resize-observer';
import { ConnectFacebookModalComponent } from '@fb-tool-front/shared/components/connect-facebook-modal/connect-facebook-modal.component';

@NgModule({
  declarations: [
    SecondsToMinutesPipe,
    SafePipe,
    BytesToSizePipe,
    OnlyCountriesPipe,
    ComingSoonCardComponent,
    DemoRequestComponent,
    NothingFoundComponent,
    MobileModalComponent,
    YoutubeEmbeddedComponent,
    LazyImageComponent,
    TrimDirective,
    AmplitudeDirective,
    GtmDirective,
    TabsIosDirective,
    GravityButtonComponent,
    FilterPanelComponent,
    ThemeNightToggleDirective,
    LocaleToggleDirective,
    CookiesModalComponent,
    DeleteAccountModalComponent,
    PricingModalComponent,
    OverlayRuleComponent,
    CountriesComponent,
    OverlayTagsComponent,
    OverlaySelectorComponent,
    RuleConditionsComponent,
    ConnectFacebookModalComponent
  ],
  imports: [
    TranslateModule.forChild(),
    RecaptchaModule,
    RecaptchaFormsModule,
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    FormsModule,
    TuiHostedDropdownModule,
    TuiDataListModule,
    TuiSvgModule,
    TuiButtonModule,
    TuiArrowModule,
    TuiToggleModule,
    TuiLinkModule,
    TuiIslandModule,
    TuiTabsModule,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiBadgeModule,
    CustomDialogModule,
    TuiLoaderModule,
    TuiPrimitiveTextfieldModule,
    TuiHintModule,
    TuiTooltipModule,
    TuiCheckboxBlockModule,
    TuiGroupModule,
    TuiCheckboxLabeledModule,
    TuiSelectModule,
    TuiDataListWrapperModule,
    TuiComboBoxModule,
    TuiActiveZoneModule,
    TuiInputFilesModule,
    TuiFieldErrorPipeModule,
    TuiMultiSelectModule,
    TuiDropdownModule,
    TuiAccordionModule,
    TuiSidebarModule,
    TuiInputCountModule,
    TuiRadioBlockModule,
    TuiInputNumberModule,
    TuiInputDateModule,
    TuiLabelModule,
    AitColorSelectorModule,
    AitInputColorModule,
    AitColorPickerModule,
    AitColorOptionsControllerModule,
    AitColorEditModule,
    AitPaletteModule,
    TuiFocusableModule,
    TuiCheckboxLabeledModule,
    TuiFilterPipeModule,
    TuiSelectModule,
    TuiComboBoxModule,
    TuiDataListWrapperModule,
    TuiNotificationModule,
    TuiLetModule,
    TuiErrorModule,
    TuiTextAreaModule,
    DragDropModule,
    TuiMultiSelectModule,
    TuiHoveredModule,
    TuiScrollbarModule,
    TuiCheckboxModule,
    TuiTableModule,
    IntersectionObserverModule,
    TuiLineClampModule,
    TuiLazyLoadingModule, // @badVendor TODO: push an issue to Taiga github
    ScrollingModule,
    TuiInputSliderModule,
    TuiFilterByInputPipeModule,
    TuiInputTagModule,
    TuiInputPasswordModule,
    TuiBadgedContentModule,
    TuiForModule,
    TuiPreventDefaultModule,
    TuiHintModule,
    TuiProgressModule,
    TuiTagModule,
    TuiInputPhoneInternationalModule,
    TuiInputInlineModule,
    TuiAutoFocusModule,
    TuiPaletteModule,
    TuiMobileTabsModule,
    PolymorpheusModule,
    TuiCurrencyPipeModule,
    TuiInputDateRangeModule,
    TuiLineDaysChartModule,
    TuiAxesModule,
    TuiLineChartModule,
    TuiMapperPipeModule,
    NgChartsModule,
    TuiDataListDropdownManagerModule,
    TuiPrimitiveCheckboxModule,
    ResizeObserverModule,
    TuiElementModule,
    TuiEditorModule,
    TuiRadioListModule,
    TuiInputDateTimeModule,
    TuiInputTimeModule,
    TuiTilesModule
  ],
  exports: [
    RecaptchaModule,
    RecaptchaFormsModule,
    CommonModule,
    TranslateModule,
    GravityButtonComponent,
    ComingSoonCardComponent,
    NothingFoundComponent,
    MobileModalComponent,
    YoutubeEmbeddedComponent,
    DemoRequestComponent,
    PricingModalComponent,
    LazyImageComponent,
    OverlayRuleComponent,
    CountriesComponent,
    OverlayTagsComponent,
    OverlaySelectorComponent,
    ConnectFacebookModalComponent,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    SecondsToMinutesPipe,
    BytesToSizePipe,
    OnlyCountriesPipe,
    SafePipe,
    TuiHostedDropdownModule,
    TuiDataListModule,
    TuiSvgModule,
    TuiButtonModule,
    TuiArrowModule,
    TuiToggleModule,
    TuiLinkModule,
    TuiIslandModule,
    TuiTabsModule,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiBadgeModule,
    CustomDialogModule,
    TuiLoaderModule,
    TuiPrimitiveTextfieldModule,
    TuiHintModule,
    TuiTooltipModule,
    TuiCheckboxBlockModule,
    TuiGroupModule,
    TuiCheckboxLabeledModule,
    TuiSelectModule,
    TuiDataListWrapperModule,
    TuiComboBoxModule,
    TuiActiveZoneModule,
    TuiInputFilesModule,
    TuiFieldErrorPipeModule,
    TuiMultiSelectModule,
    TuiDropdownModule,
    TuiAccordionModule,
    TuiSidebarModule,
    TuiInputCountModule,
    TuiInputDateModule,
    TuiRadioBlockModule,
    TuiInputNumberModule,
    TuiLabelModule,
    AitColorSelectorModule,
    AitInputColorModule,
    AitColorPickerModule,
    AitColorOptionsControllerModule,
    AitColorEditModule,
    AitPaletteModule,
    TuiFocusableModule,
    TuiCheckboxLabeledModule,
    TuiFilterPipeModule,
    TuiComboBoxModule,
    TuiDataListWrapperModule,
    TuiNotificationModule,
    TuiLetModule,
    TuiErrorModule,
    TuiTextAreaModule,
    DragDropModule,
    TuiMultiSelectModule,
    TuiHoveredModule,
    TrimDirective,
    AmplitudeDirective,
    GtmDirective,
    TabsIosDirective,
    TuiScrollbarModule,
    TuiCheckboxModule,
    TuiTableModule,
    IntersectionObserverModule,
    TuiLineClampModule,
    TuiLazyLoadingModule,
    ScrollingModule,
    TuiInputSliderModule,
    TuiFilterByInputPipeModule,
    TuiInputTagModule,
    TuiInputPasswordModule,
    TuiBadgedContentModule,
    FilterPanelComponent,
    TuiForModule,
    TuiPreventDefaultModule,
    ThemeNightToggleDirective,
    LocaleToggleDirective,
    TuiHintModule,
    TuiProgressModule,
    TuiTagModule,
    TuiInputPhoneInternationalModule,
    TuiInputInlineModule,
    TuiAutoFocusModule,
    RuleConditionsComponent,
    TuiPaletteModule,
    TuiMobileTabsModule,
    PolymorpheusModule,
    TuiCurrencyPipeModule,
    TuiInputDateRangeModule,
    TuiLineDaysChartModule,
    TuiAxesModule,
    TuiLineChartModule,
    TuiMapperPipeModule,
    NgChartsModule,
    TuiDataListDropdownManagerModule,
    TuiPrimitiveCheckboxModule,
    ResizeObserverModule,
    TuiElementModule,
    TuiEditorModule,
    TuiRadioListModule,
    TuiInputDateTimeModule,
    TuiInputTimeModule,
    TuiTilesModule
  ]
})
export class SharedModule {}
